import { getLocalizedAgentName, getLocalizedAgentDesc, getLocalizedAgentCategory, getLocalizedAgentFeatures, formatPriceWithCurrency, Currency } from '../utils/i18n';
import React, { useState } from 'react';
import { 
  Sparkles, ArrowLeft, ArrowRight, ShieldCheck, Crown, 
  Eye, Mic, UserCheck, Sparkles as MagicIcon, Lock, Zap, HelpCircle
} from 'lucide-react';
import * as Icons from 'lucide-react';
import agentsConfig from '../agentsConfig.json';

interface AdminCenterHubViewProps {
  onLaunchAgent: (target: string) => void;
  onBack: () => void;
  language: 'uk' | 'en' | 'pl';
  currency?: Currency;
  currentUserEmail?: string;
}

export const ADMIN_AGENT_IDS = [
  'pharma-advisor',
  'admin-lead-harvester',
  'meeting-transcriber',
  'nonverbal-profiler',
  'folk-healer-traditions',
  'executive-project-orchestrator',
  'global-foresight-strategist',
  'nis-eco-situational-analyst',
  'nis-eco-pro-orchestrator',
  'gcsk-infrasound-air-defense-coordinator',
  'gcsk-infrasound-spectral-auditor',
  'gcsk-infrasound-blast-analyst'
];

const AgentIcon = ({ name, className }: { name: string; className?: string }) => {
  const IconComponent = (Icons as any)[name];
  if (!IconComponent) return <Crown className={className} />;
  return <IconComponent className={className} />;
};

export default function AdminCenterHubView({
  onLaunchAgent,
  onBack,
  language, currentUserEmail, currency = 'UAH'
}: AdminCenterHubViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isAdmin = ['bbass776@gmail.com', 'irinacosmos777@gmail.com'].includes(currentUserEmail?.toLowerCase() || '');
  const [selectedSubTab, setSelectedSubTab] = useState<'all' | 'nis-eco' | 'gcsk-infrasound' | 'strategist' | 'orchestrator' | 'pharma' | 'profiling' | 'audio' | 'harvesting' | 'special'>('all');

  if (!isAdmin) {
    return (
      <div className="p-8 text-center bg-slate-900 border border-red-500/30 rounded-2xl text-white font-mono space-y-4 max-w-xl mx-auto my-12 shadow-2xl">
        <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto text-red-400">
          <Lock className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-red-400">
          {isPl ? '⛔ Dostęp ograniczony' : isUk ? '⛔ Доступ обмежено' : '⛔ Access Restricted'}
        </h2>
        <p className="text-sm text-slate-300">
          {isPl 
            ? 'Centrum Specjalnych Modułów Administracyjnych jest dostępne wyłącznie dla administratorów platformy.' 
            : isUk 
            ? 'Центр Спеціальних Адмін-Агентів доступний виключно для адміністраторів платформи.' 
            : 'The Admin Center is restricted to platform administrators.'}
        </p>
        <button 
          onClick={onBack}
          className="px-5 py-2.5 bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold uppercase rounded-xl transition-all cursor-pointer border border-slate-700 inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>
      </div>
    );
  }

  const allAdminAgents = agentsConfig.filter((a: any) => ADMIN_AGENT_IDS.includes(a.id));

  const filteredAgents = allAdminAgents.filter((agent: any) => {
    if (selectedSubTab === 'all') return true;
    if (selectedSubTab === 'nis-eco') return agent.id === 'nis-eco-situational-analyst' || agent.id === 'nis-eco-pro-orchestrator';
    if (selectedSubTab === 'gcsk-infrasound') return agent.id.startsWith('gcsk-infrasound-');
    if (selectedSubTab === 'strategist') return agent.id === 'global-foresight-strategist';
    if (selectedSubTab === 'orchestrator') return agent.id === 'executive-project-orchestrator';
    if (selectedSubTab === 'pharma') return agent.id === 'pharma-advisor';
    if (selectedSubTab === 'profiling') return agent.id === 'nonverbal-profiler';
    if (selectedSubTab === 'audio') return agent.id === 'meeting-transcriber';
    if (selectedSubTab === 'harvesting') return agent.id === 'admin-lead-harvester';
    if (selectedSubTab === 'special') return agent.id === 'folk-healer-traditions';
    return true;
  });

  return (
    <div className="space-y-8 animate-fade-in pb-12 font-mono">
      {/* Header Bar */}
      <div className="flex items-center justify-between gap-4 flex-wrap bg-slate-900 border border-amber-500/30 rounded-xl p-4 text-white shadow-lg">
        <button
          onClick={onBack}
          className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-amber-300 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-amber-500/40"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{isPl ? '← Powrót do Panelu Głównego' : isUk ? '← Назад до Головного Хабу' : '← Back to Main Hub'}</span>
        </button>

        <div className="flex items-center gap-2">
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/40 rounded-md flex items-center gap-1">
            <Crown className="w-3.5 h-3.5 text-amber-400" />
            {isPl ? '👑 CENTRUM MODUŁÓW ADMINA' : isUk ? '👑 ЦЕНТР СПЕЦІАЛЬНИХ АДМІН-АГЕНТІВ' : '👑 SPECIAL ADMIN CENTER'}
          </span>
          <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded-md">
            🔒 RESTRICTED ACCESS
          </span>
        </div>
      </div>

      {/* Hero Banner */}
      <div className="relative bg-gradient-to-r from-slate-950 via-amber-950 to-slate-900 border border-amber-500/50 rounded-2xl p-6 sm:p-8 text-white shadow-2xl overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="relative z-10 max-w-3xl space-y-3">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 text-[9px] font-extrabold uppercase bg-amber-500/30 text-amber-300 border border-amber-400/40 rounded">
              {isPl ? 'PROFILOWANIE • DETEKCJA KŁAMSTWA • KINEZYKA • TRANSKRYPCJA • LEAD GENERATION' : isUk ? 'ПРОФАЙЛІНГ • ДЕТЕКЦІЯ БРЕХНІ • КІНЕСИКА • ТРАНСКРИБАЦІЯ • ЛІДИ' : 'PROFILING • LIE DETECTION • KINESICS • TRANSCRIPTION • LEADS'}
            </span>
          </div>

          <h1 className="text-2xl sm:text-4xl font-extrabold tracking-tight uppercase leading-tight bg-gradient-to-r from-amber-200 via-yellow-100 to-amber-400 bg-clip-text text-transparent">
            {isPl 
              ? 'CENTRUM SPECJALNYCH NARZĘDZI ADMINISTRACYJNYCH' 
              : isUk 
              ? 'ЦЕНТР СПЕЦІАЛЬНИХ АДМІНІСТРАТИВНИХ ІНСТРУМЕНТІВ' 
              : 'SPECIAL ADMINISTRATIVE TOOL CENTER'}
          </h1>

          <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
            {isPl 
              ? 'Ekskluzywny panel zarządzania specjalnymi narzędziami: profilowanie niewerbalne i weryfikacja szczerości FACS, transkrypcja rozmów z diaryzacją mówców, parser kontaktów oraz moduł fitoterapii.'
              : isUk 
              ? 'Ексклюзивна панель керування спеціалізованими інструментами: невербальний профайлінг та верифікація щирості за мікромімікою (FACS), транскрибація переговорів з дійаризацією мовців, парсер контактів із форумами та аналіз цілительства.'
              : 'Exclusive management center for specialized admin tools: nonverbal profiling and lie detection (Paul Ekman FACS), meeting audio transcription with speaker diarization, contact harvester, and folk tradition analysis.'}
          </p>
        </div>
      </div>

      {/* Flagship Assistant Terminal Card */}
      <div className="bg-gradient-to-r from-emerald-950/80 via-slate-900 to-teal-950/80 border-2 border-emerald-500/50 rounded-2xl p-6 shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2 max-w-2xl">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/40 rounded text-[10px] font-extrabold uppercase flex items-center gap-1">
              <Zap className="w-3 h-3 text-emerald-400" />
              {isPl ? 'FLAGOWY MODUŁ ADMINISTRATORA' : isUk ? 'ФЛАГМАНСЬКИЙ МОДУЛЬ АДМІНІСТРАТОРА' : 'FLAGSHIP ADMIN MODULE'}
            </span>
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded text-[10px] font-bold">
              ☁️ GOOGLE DRIVE SYNC READY
            </span>
          </div>

          <h2 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-teal-200">
              🤖 {isPl ? 'Asystent AI Antigravity Core' : isUk ? 'ШІ-Асистент Розробника Antigravity Core' : 'Antigravity Core AI Assistant'}
            </span>
          </h2>

          <p className="text-xs text-slate-300 leading-relaxed">
            {isPl
              ? 'Prywatna konsola administratora: wprowadzaj dowolne zadania inżynieryjne, wgrywaj pliki dowolnego typu (Word, Excel, PDF, kod, obrazy), generuj oficjalne raporty DSTU i tabele z formułami z natychmiastowym pobraniem na komputer oraz automatycznym zapisem na Google Drive w 1 kliknięcie.'
              : isUk
              ? 'Персональна консоль адміністратора: постановка інженерних завдань будь-якої складності, підвантаження файлів будь-яких форматів (Word, Excel, PDF, код, фото, архіви), двосторонній обмін документами, миттєве збереження на комп\'ютер та синхронізація з Google Drive у 1 клік.'
              : 'Personal administrator console: direct tasking, multi-format file uploads (Word, Excel, PDF, code, images, archives), bidirectional document generation, instant download to PC and 1-click Google Drive synchronization.'}
          </p>
        </div>

        <button
          onClick={() => onLaunchAgent('admin-assistant')}
          className="px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xl shadow-emerald-500/25 flex items-center gap-2.5 cursor-pointer shrink-0 border border-emerald-300"
        >
          <Sparkles className="w-4 h-4 text-slate-950" />
          <span>{isPl ? '🚀 Otwórz Konsolę Asystenta' : isUk ? '🚀 Відкрити Консоль Асистента' : '🚀 Open Assistant Console'}</span>
        </button>
      </div>

      {/* Flagship Trembita-MESH Tactical Radar Card */}
      <div className="bg-gradient-to-r from-red-950/80 via-slate-900 to-rose-950/80 border-2 border-red-500/50 rounded-2xl p-6 shadow-2xl relative overflow-hidden flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div className="space-y-2 max-w-2xl">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-0.5 bg-red-500/20 text-red-400 border border-red-500/40 rounded text-[10px] font-extrabold uppercase flex items-center gap-1">
              <Icons.Radar className="w-3 h-3 text-red-400" />
              {isPl ? 'PROJEKT OBRONY PRZECIWLOTNICZEJ' : isUk ? 'ПРОЄКТ ППО ТА ТАКТИЧНОЇ ОБРОБКИ' : 'AIR DEFENSE TACTICAL PROJECT'}
            </span>
            <span className="px-2 py-0.5 bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded text-[10px] font-bold">
              📡 TDoA 3D RADAR + LEAD POINT
            </span>
          </div>

          <h2 className="text-xl sm:text-2xl font-black text-white flex items-center gap-2.5">
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-400 via-rose-300 to-amber-200">
              📡 {isPl ? 'Taktyczny Kompleks «Trembita-MESH»' : isUk ? 'Тактичний Комплекс ППО «Трембіта-МЕШ»' : '«Trembita-MESH» Air Defense Tactical Suite'}
            </span>
          </h2>

          <p className="text-xs text-slate-300 leading-relaxed">
            {isPl
              ? 'Interaktywny radar obrony powietrznej w czasie rzeczywistym: 3D śledzenie celów Shahed-136 (dokładność 0.38 m), wykrywanie startu rakiet balistycznych (0.1–5 Hz), automatyczne wyznaczanie punktu wyprzedzenia (Lead Point) dla CKM Browning M2 / DShK oraz kąta reflektorów OPL, monitoring 6 węzłów LoRa/Wi-Fi.'
              : isUk
              ? 'Інтерактивний тактичний екран ППО у реальному часі: 3D-трекінг ворожих Shahed-136 за TDoA (точність 0.38 м, швидкість 184 км/год, висота 135 м, ETA), засікання старту балістичних ракет (0.1–5 Гц), автоматична цілевказівка для прожекторів та кулеметів МВГ (Lead Point) та моніторинг 6 вузлів мережі.'
              : 'Interactive real-time air defense radar: 3D TDoA tracking of Shahed-136 drones (0.38m precision, speed 184 km/h, alt 135m, ETA), ballistic missile launch detection (0.1–5 Hz infrasound), automated fire direction for searchlights and machine guns (Lead Point calculation), and 6-node MESH network health.'}
          </p>
        </div>

        <button
          onClick={() => onLaunchAgent('trembita-mesh')}
          className="px-6 py-3.5 bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-500 hover:to-rose-500 text-white font-black text-xs uppercase tracking-wider rounded-xl transition-all shadow-xl shadow-red-500/25 flex items-center gap-2.5 cursor-pointer shrink-0 border border-red-400"
        >
          <Icons.Radar className="w-4 h-4 text-white" />
          <span>{isPl ? '🚀 Otwórz Trembita-MESH' : isUk ? '🚀 Відкрити Трембіта-МЕШ' : '🚀 Open Trembita-MESH'}</span>
        </button>
      </div>

      {/* Subtab Filter */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 border-b border-slate-800">
        <button
          onClick={() => setSelectedSubTab('all')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer ${
            selectedSubTab === 'all'
              ? 'bg-amber-600 text-slate-950 shadow-md shadow-amber-600/20'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          {isPl ? 'Wszyscy Agenci Admina' : isUk ? 'Усі Адмін-Агенти' : 'All Admin Agents'} ({allAdminAgents.length})
        </button>

        <button
          onClick={() => setSelectedSubTab('nis-eco')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'nis-eco'
              ? 'bg-emerald-500 text-slate-950 shadow-md shadow-emerald-500/20 font-black'
              : 'bg-slate-900 text-emerald-300 hover:bg-slate-800 border border-emerald-500/40'
          }`}
        >
          <span>🛰️ {isPl ? '«NIS-EKO» Centrum Sytuacyjne' : isUk ? '«НІС-ЕКО» Ситуаційний Центр' : '«NIS-ECO» Situational Center'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('gcsk-infrasound')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'gcsk-infrasound'
              ? 'bg-cyan-500 text-slate-950 shadow-md shadow-cyan-500/20 font-black'
              : 'bg-slate-900 text-cyan-300 hover:bg-slate-800 border border-cyan-500/40'
          }`}
        >
          <span>📡 {isPl ? '«GCSS» Infradźwiękowa Sieć OPL' : isUk ? '«ГЦСК» Інфразвукова Мережа ППО' : '«GCSK» Infrasound Air Defense'} (3)</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('strategist')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'strategist'
              ? 'bg-amber-600 text-slate-950 shadow-md shadow-amber-600/20'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <span>🧭 {isPl ? 'Globalny Strateg CSO' : isUk ? 'Головний Стратег & Форсайт' : 'Global Foresight CSO'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('orchestrator')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'orchestrator'
              ? 'bg-amber-600 text-slate-950 shadow-md shadow-amber-600/20'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <span>🎯 {isPl ? 'Orkiestrator Zadań' : isUk ? 'Головний Оркестратор' : 'Task Orchestrator'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('pharma')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'pharma'
              ? 'bg-amber-600 text-slate-950 shadow-md shadow-amber-600/20'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <span>💊 {isPl ? 'Farmacja & Leki' : isUk ? 'Фармацевтика & Ліки' : 'Pharmacology & Medicines'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('profiling')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'profiling'
              ? 'bg-amber-600 text-slate-950 shadow-md shadow-amber-600/20'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Eye className="w-3.5 h-3.5" />
          <span>{isPl ? '👁️ Profilowanie & Kłamstwo' : isUk ? '👁️ Профайлінг & Детекція Брехні' : '👁️ Profiling & Lie Detection'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('audio')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'audio'
              ? 'bg-amber-600 text-slate-950 shadow-md shadow-amber-600/20'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <Mic className="w-3.5 h-3.5" />
          <span>{isPl ? '🎙️ Transkrypcja Spotkań' : isUk ? '🎙️ Транскрибатор Переговорів' : '🎙️ Meeting Transcriber'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('harvesting')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'harvesting'
              ? 'bg-amber-600 text-slate-950 shadow-md shadow-amber-600/20'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <UserCheck className="w-3.5 h-3.5" />
          <span>{isPl ? '🔍 Pozyskiwanie Kontaktów' : isUk ? '🔍 Набір Контактів & Форуми' : '🔍 Lead Harvester'}</span>
        </button>

        <button
          onClick={() => setSelectedSubTab('special')}
          className={`px-4 py-2 text-xs font-extrabold uppercase rounded-xl transition-all whitespace-nowrap cursor-pointer flex items-center gap-1.5 ${
            selectedSubTab === 'special'
              ? 'bg-amber-600 text-slate-950 shadow-md shadow-amber-600/20'
              : 'bg-slate-900 text-slate-300 hover:bg-slate-800 border border-slate-800'
          }`}
        >
          <MagicIcon className="w-3.5 h-3.5" />
          <span>{isPl ? '🌿 Ziołolecznictwo & Tradycje' : isUk ? '🌿 Травництво & Цілительство' : '🌿 Folk Traditions'}</span>
        </button>
      </div>

      {/* Agents Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredAgents.map((agent: any) => (
          <div
            key={agent.id}
            className="bg-slate-900 border border-amber-500/30 hover:border-amber-400/60 rounded-2xl p-6 text-white shadow-xl transition-all flex flex-col justify-between space-y-4 hover:shadow-amber-500/10"
          >
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <span className="px-2.5 py-1 text-[10px] font-extrabold uppercase bg-amber-500/20 text-amber-300 border border-amber-400/30 rounded-md flex items-center gap-1">
                  <AgentIcon name={agent.icon} className="w-3.5 h-3.5 text-amber-400" />
                  {getLocalizedAgentCategory(agent, language)}
                </span>
                <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-red-500/20 text-red-300 border border-red-400/30 rounded">
                  👑 ADMIN ONLY
                </span>
              </div>

              <h3 className="text-lg font-extrabold text-amber-200 uppercase tracking-tight flex items-center gap-2">
                <span>{getLocalizedAgentName(agent, language)}</span>
              </h3>

              <p className="text-xs text-slate-300 leading-relaxed">
                {getLocalizedAgentDesc(agent, language)}
              </p>

              <div className="space-y-1.5 pt-2">
                <span className="text-[10px] font-extrabold text-amber-400/80 uppercase">
                  {isPl ? 'Kluczowe możliwości:' : isUk ? 'Ключові можливості:' : 'Key Features:'}
                </span>
                <ul className="space-y-1">
                  {getLocalizedAgentFeatures(agent, language).map((feature: string, idx: number) => (
                    <li key={idx} className="text-xs text-slate-300 flex items-start gap-2">
                      <span className="text-amber-400 text-xs font-bold mt-0.5">•</span>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="pt-4 border-t border-slate-800 flex items-center justify-between gap-4">
              <div className="text-[11px] text-slate-400">
                <span className="font-bold text-amber-300">{isPl ? 'Model:' : isUk ? 'Модель:' : 'Model:'}</span> {agent.model || 'gemini-3.7-flash'}
              </div>
              <button
                onClick={() => onLaunchAgent(agent.target || agent.id)}
                className="px-5 py-2.5 bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500 text-slate-950 font-extrabold text-xs uppercase rounded-xl transition-all shadow-md shadow-amber-600/20 flex items-center gap-2 cursor-pointer hover:scale-[1.02]"
              >
                <span>{isPl ? '🚀 Uruchom Zadanie' : isUk ? '🚀 Виконати Задачу' : '🚀 Execute Task'}</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

